import toast from 'react-hot-toast'

import {
  buildFetchOptionsWithAuth,
  fetchJson,
  useMutation,
  useQueryClient,
} from '@fv/client-core'
import type { LoadDocumentSource, LoadDocumentType } from '@fv/models'

import { apiUri } from '@/constants'
import { adminFetch } from '@/utils'

async function uploadLoadDocument(dto: {
  file: string | Blob
  loadId: string
  source: LoadDocumentSource
  type: LoadDocumentType
}) {
  const endpoint = `${apiUri}/loads/${dto.loadId}/documents`
  const formData = new FormData()
  formData.append('file', dto.file)

  const options = buildFetchOptionsWithAuth({
    body: formData,
    headers: {
      'x-document-source': dto.source,
      'x-document-type': dto.type,
    },
    method: 'POST',
  })

  // Content-type set automatically from `formData`
  delete options.headers['Content-type']

  const response = await fetchJson(endpoint, options)
  if (response.ok) return response.json
  throw response.errorMessage
}

export function useUploadLoadDocument() {
  const queryClient = useQueryClient()

  const mutation = useMutation(uploadLoadDocument, {
    onError: (error: { message: string }) => {
      toast.error(error.message)
      mutation.reset()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['missing-bol-list'] })
      queryClient.invalidateQueries({ queryKey: ['dispatch-list'] })
      toast.success('Document uploaded successfully.')
      mutation.reset()
    },
  })

  return mutation
}

async function deleteLoadDocument(dto: {
  documentId: string
  loadId: string
}): Promise<{ documentId: string; loadId: string }> {
  return adminFetch(`/loads/${dto.loadId}/documents/${dto.documentId}`, {
    method: 'DELETE',
  })
}

export function useDeleteLoadDocument() {
  const queryClient = useQueryClient()

  return useMutation(deleteLoadDocument, {
    onSuccess: (res: { documentId: string; loadId: string }) => {
      queryClient.setQueriesData(
        {
          queryKey: ['load', res.loadId],
        },
        (prev?: any) => {
          return (
            prev && {
              ...prev,
              documents: (prev.documents ?? []).filter(
                (d: any) => d._id !== res.documentId,
              ),
            }
          )
        },
      )

      toast.success('Document deleted successfully.')
    },
  })
}
